import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Instagram, LayoutDashboard, Linkedin, Twitter, Youtube } from "lucide-react";
import { api, getUser } from "@/lib/api";

export function homePathFor(u) {
  if (!u) return "/";
  return u.role === "admin" ? "/admin" : u.content_manager ? "/content-manager" : "/dashboard";
}

export function Logo({ className = "", to }) {
  const user = getUser();
  const dest = to || homePathFor(user);
  return (
    <Link
      to={dest}
      data-testid="brand-logo"
      className={`inline-flex items-center ${className}`}
    >
      <img
        src="/noobsync-wordmark.png"
        alt="NoobSync"
        className="h-8 w-auto"
      />
    </Link>
  );
}

export function SiteHeader() {
  const user = getUser();
  return (
    <header className="sticky top-0 z-40 border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
        <Logo />
        <nav className="hidden items-center gap-7 text-sm text-zinc-400 md:flex">
          <a href="/#what-you-do" data-testid="nav-what-you-do" className="hover:text-white">
            What you do
          </a>
          <a href="/#perks" data-testid="nav-perks" className="hover:text-white">
            What you get
          </a>
          <a href="/#how-it-works" data-testid="nav-how" className="hover:text-white">
            How it works
          </a>
          <Link to="/leaderboard" data-testid="nav-leaderboard" className="hover:text-white">
            Leaderboard
          </Link>
          <a href="/#faq" data-testid="nav-faq" className="hover:text-white">
            FAQ
          </a>
        </nav>
        <div className="flex items-center gap-2">
          {user ? (
            <Link
              to={homePathFor(user)}
              data-testid="header-dashboard-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white transition-all hover:-translate-y-0.5"
            >
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden sm:inline">{user.role === "admin" ? "Admin panel" : "My dashboard"}</span>
              <span className="sm:hidden">Dashboard</span>
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="header-login-btn"
                className="inline-flex rounded-md brand-gradient-bg px-4 py-2 text-sm font-bold text-white transition-all hover:-translate-y-0.5 md:bg-none md:border md:border-zinc-800 md:font-normal md:text-zinc-300 md:hover:translate-y-0 md:hover:border-zinc-700 md:hover:text-white"
              >
                <span className="md:hidden">Login</span>
                <span className="hidden md:inline">Ambassador login</span>
              </Link>
              <Link
                to="/apply"
                data-testid="header-apply-btn"
                className="hidden rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-4px_rgba(204,255,0,0.45)] md:inline-flex"
              >
                Apply
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function SiteFooter() {
  const [socials, setSocials] = useState({});
  useEffect(() => {
    api.get("/site-settings").then((r) => setSocials(r.data?.socials || {})).catch(() => {});
  }, []);

  const SOCIAL_ICONS = { instagram: Instagram, linkedin: Linkedin, twitter: Twitter, youtube: Youtube };
  const NAV_LINKS = [
    { label: "Apply", href: "/apply", isAnchor: false },
    { label: "Leaderboard", href: "#leaderboard", isAnchor: true },
    { label: "How it works", href: "#how-it-works", isAnchor: true },
    { label: "FAQ", href: "#faq", isAnchor: true },
  ];

  const linkCls = "block py-1 text-sm text-zinc-400 transition hover:text-[#A855F7] hover:translate-x-0.5";
  const headingCls = "text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500";

  return (
    <footer className="relative border-t border-zinc-900 bg-black">
      {/* subtle gradient top border */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#A855F7]/40 to-transparent" />
      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-16">
        <div className="grid gap-10 sm:grid-cols-2 md:grid-cols-4 md:gap-12">
          {/* Brand */}
          <div className="md:pr-4">
            <Logo />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-zinc-500">
              The Brand Ambassador Program — part of NoobSync Innovations.
            </p>
            <a
              href="https://noobsync.com"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-back-to-main"
              className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-zinc-500 transition hover:text-[#A855F7]"
            >
              <ArrowLeft className="h-3 w-3" /> Back to noobsync.com
            </a>
          </div>

          {/* Navigate (anchor links on landing) */}
          <div>
            <p className={headingCls}>Navigate</p>
            <ul className="mt-4 space-y-1">
              {NAV_LINKS.map((n) => (
                <li key={n.label}>
                  {n.isAnchor ? (
                    <a href={n.href} data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls}>
                      {n.label}
                    </a>
                  ) : (
                    <Link to={n.href} data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls}>
                      {n.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <p className={headingCls}>Legal</p>
            <ul className="mt-4 space-y-1">
              <li><Link to="/privacy" data-testid="footer-legal-privacy" className={linkCls}>Privacy</Link></li>
              <li><Link to="/terms" data-testid="footer-legal-terms" className={linkCls}>Terms</Link></li>
              <li><Link to="/guidelines" data-testid="footer-legal-guidelines" className={linkCls}>Guidelines</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className={headingCls}>Contact</p>
            <ul className="mt-4 space-y-1">
              <li>
                <a href="mailto:info@noobsync.com" data-testid="footer-contact-email" className={linkCls}>
                  info@noobsync.com
                </a>
              </li>
            </ul>
            {Object.keys(socials).length > 0 && (
              <div className="mt-4 flex items-center gap-3" data-testid="footer-socials">
                {Object.entries(socials).map(([platform, val]) => {
                  const Icon = SOCIAL_ICONS[platform];
                  const url = typeof val === "string" ? val : val?.url;
                  if (!Icon || !url) return null;
                  return (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      title={platform}
                      data-testid={`footer-social-${platform}`}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-zinc-800 text-zinc-500 transition hover:border-[#A855F7]/60 hover:bg-[#A855F7]/10 hover:text-[#A855F7]"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="mt-12 border-t border-zinc-900 pt-6">
          <p className="text-xs text-zinc-600">
            © {new Date().getFullYear()} NoobSync Innovations. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
